// The `ci_run` wire shape — one projection, consumed by `GET /v1/ci/runs`
// (`apps/api/src/routes/ci.ts`) and the MCP `ci` verb's `runs` action
// (`packages/mcp/src/army/ci.ts`).
//
// The two doors had drifted, and in the direction the resource layer exists to
// stop: `projectCiRun` published 16 keys, `maskCiJob` published 14. The REST
// body carried `refusal_reason` and `duration_ms`; the MCP body did not, so an
// agent asking the platform why a run was refused got `status: 'refused'` and
// nothing else, while a dashboard reading the same row got the reason. Neither
// projection was typed against the other — the MCP copy returned
// `Record<string, unknown>` — so nothing could have reported it.
//
// The union is the REST superset. Adding the two fields to the MCP body is
// additive on that door (an agent reading a JSON object gains two keys);
// DROPPING them from REST would have been a breaking change for a paying
// client, and a resource that serialises the intersection would have made the
// smaller body the contract. Shape is forever, so the merge only ever grows the
// narrower door.

import { z } from 'zod';
import {
  CiJobConclusionSchema,
  CiJobStatusSchema,
  CiRefusalReasonSchema,
} from '../entities/ci-job';
import { defineResource } from './resource';

/**
 * The camelCase shape the projection reads. Structural, so the resource states
 * its own minimum rather than pulling in `CiJob`'s full schema (`safety`,
 * `accountId` and `updatedAt` are read by neither door); `ci-run.test.ts`
 * proves a parsed `CiJob` satisfies it.
 */
export type CiRunEntity = {
  id: string;
  repoFullName: string;
  githubJobId: number;
  githubRunId: number | null;
  labels: string[];
  status: z.infer<typeof CiJobStatusSchema>;
  conclusion: z.infer<typeof CiJobConclusionSchema> | null;
  refusalReason: z.infer<typeof CiRefusalReasonSchema> | null;
  runnerId: string | null;
  logRef: string | null;
  logExcerpt: string | null;
  queuedAt: Date | null;
  assignedAt: Date | null;
  startedAt: Date | null;
  completedAt: Date | null;
  createdAt: Date;
};

/** The published body. Field names and nullability are the existing contract. */
export const CiRunWireSchema = z.object({
  id: z.string(),
  repo_full_name: z.string(),
  github_job_id: z.number(),
  github_run_id: z.number().nullable(),
  labels: z.array(z.string()),
  status: CiJobStatusSchema,
  conclusion: CiJobConclusionSchema.nullable(),
  /**
   * Why a refused run was declined (`permission_missing` — the org that has not
   * re-accepted the `organization_self_hosted_runners` permission). Null on any
   * non-refused run, and on a refused run predating the field.
   */
  refusal_reason: CiRefusalReasonSchema.nullable(),
  /** The box (`runners.id`) that ran it; null until assigned / after box delete. */
  runner_id: z.string().nullable(),
  /** `<2KB` failure-tail excerpt, inline so a caller sees WHY without a fetch. */
  log_excerpt: z.string().nullable(),
  /** Whether `…/log-url` can presign. The object key (`log_ref`) never crosses. */
  has_log: z.boolean(),
  queued_iso: z.string().nullable(),
  assigned_iso: z.string().nullable(),
  started_iso: z.string().nullable(),
  completed_iso: z.string().nullable(),
  /**
   * Wall-clock runtime once both edges land; null while running/queued or if
   * either timestamp is missing — never a negative number.
   */
  duration_ms: z.number().nullable(),
  created_iso: z.string(),
});

/** What a client should type against for a CI-run body. */
export type CiRunWire = z.infer<typeof CiRunWireSchema>;

export const CiRunResource = defineResource<CiRunEntity, typeof CiRunWireSchema>({
  schema: CiRunWireSchema,
  serialize: (j) => ({
    id: j.id,
    repo_full_name: j.repoFullName,
    github_job_id: j.githubJobId,
    github_run_id: j.githubRunId,
    labels: j.labels,
    status: j.status,
    conclusion: j.conclusion,
    refusal_reason: j.refusalReason,
    runner_id: j.runnerId,
    log_excerpt: j.logExcerpt,
    has_log: j.logRef !== null,
    queued_iso: j.queuedAt ? j.queuedAt.toISOString() : null,
    assigned_iso: j.assignedAt ? j.assignedAt.toISOString() : null,
    started_iso: j.startedAt ? j.startedAt.toISOString() : null,
    completed_iso: j.completedAt ? j.completedAt.toISOString() : null,
    duration_ms:
      j.startedAt && j.completedAt ? j.completedAt.getTime() - j.startedAt.getTime() : null,
    created_iso: j.createdAt.toISOString(),
  }),
});
